import { createApp } from "vue";
import App from "./App.vue";
import Home from "./Home.vue";
import InstallSuccess from "./InstallSuccess.vue";
import SignIn from "./SignIn.vue";
import Leaderboard from "./Leaderboard.vue";
import Dashboard from "./Dashboard.vue";
import RewardsStore from "./RewardsStore.vue";
import Admin from "./Admin.vue";
import People from "./People.vue";
import AdminRewards from "./AdminRewards.vue";
import AdminRedemptions from "./AdminRedemptions.vue";
import MyRedemptions from "./MyRedemptions.vue";
import AdminGeneral from "./AdminGeneral.vue";
import HowItWorks from "./HowItWorks.vue";
import PrivacyPolicy from "./PrivacyPolicy.vue";
import {
  createWebHistory,
  createRouter,
  createWebHashHistory,
} from "vue-router";
import { useStatsStore } from "./stores/statsStore";
import { useAuthStore } from "./stores/authStore";
import { useWorkspaceStore } from "./stores/workspaceStore";
import { analytics } from "./services/analytics";

// Vuetify
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import '@mdi/font/css/materialdesignicons.css'

const vuetify = createVuetify({
  components,
  directives,
  icons: {
    defaultSet: 'mdi',
  },
})

const routes = [
  { path: "/", component: Home },
  { path: "/how-it-works", component: HowItWorks },
  { path: "/privacy-policy", component: PrivacyPolicy },
  { path: "/install-success", component: InstallSuccess },
  { path: "/sign-in", component: SignIn },
  { path: "/leaderboard", component: Leaderboard },
  { path: "/signin-success", redirect: "/dashboard"  },
  { path: "/dashboard", component: Dashboard },
  { path: "/rewards", component: RewardsStore },
  { path: "/my-redemptions", component: MyRedemptions },
  { 
    path: "/admin", 
    redirect: "/admin/people",
    meta: { requiresAdmin: true }
  },
  {
    path: "/admin/general",
    component: AdminGeneral,
    meta: { requiresAdmin: true }
  },
  {
    path: "/admin/people",
    component: People,
    meta: { requiresAdmin: true }
  },
  {
    path: "/admin/rewards",
    component: AdminRewards,
    meta: { requiresAdmin: true }
  },
  {
    path: "/admin/redemptions",
    component: AdminRedemptions,
    meta: { requiresAdmin: true }
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

// Navigation guard to check authentication
router.beforeEach(async (to, from, next) => {
  console.log('🔒 Router guard triggered:', { from: from.path, to: to.path });
  
  // Routes that don't require authentication
  const publicRoutes = ['/', '/sign-in', '/install-success', '/how-it-works', '/privacy-policy'];

  if (publicRoutes.includes(to.path)) {
    console.log('✅ Public route, allowing navigation to:', to.path);
    next();
    return;
  }

  // Add debug logging
  console.log('Checking authentication for:', to.path);
  console.log('Route meta:', to.meta);
  
  try {
    console.log('🔐 Checking authentication for protected route:', to.path);
    console.log('🌐 API Base URL:', process.env.VUE_APP_API_BASE_URL);
    
    // Check authentication directly via API call instead of using the store hook
    const authResponse = await fetch(`${process.env.VUE_APP_API_BASE_URL}/check-auth`, {
      credentials: 'include',
      headers: {
        'Accept': 'application/json'
      }
    });

    console.log('📡 Auth API response status:', authResponse.status);

    if (!authResponse.ok) {
      console.log('❌ Auth check failed, redirecting to sign-in');
      next('/sign-in');
      return;
    }

    const authData = await authResponse.json();
    console.log('✅ Auth check response:', authData);

    if (!authData.isAuthenticated) {
      console.log('❌ User not authenticated, redirecting to sign-in');
      next('/sign-in');
      return;
    }

    // Initialize stats store for authenticated users
    try {
      const { fetchStats } = useStatsStore();
      const { fetchWorkspaceEmoji } = useWorkspaceStore();
      
      // Load both stats and workspace emoji in parallel
      await Promise.all([
        fetchStats(),
        fetchWorkspaceEmoji()
      ]);
      
      console.log('Stats and workspace emoji initialized for authenticated user');
    } catch (statsError) {
      console.warn('Failed to initialize stats or workspace emoji:', statsError);
      // Don't block navigation if stats or emoji fail to load
    }

    // Check admin access for admin routes
    if (to.meta.requiresAdmin) {
      console.log('👑 Checking admin role for protected route');
      
      // The /check-auth endpoint doesn't return role, so we need to fetch it separately
      try {
        const roleResponse = await fetch(`${process.env.VUE_APP_API_BASE_URL}/user/role`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (roleResponse.ok) {
          const roleData = await roleResponse.json();
          console.log('👤 Role data from /user/role:', roleData);
          console.log('👤 User role:', roleData.role);
          console.log('👤 Role comparison:', roleData.role === 'Admin');
          
          if (roleData.role !== 'Admin') {
            console.log('❌ User is not admin, redirecting to dashboard');
            next('/dashboard');
            return;
          }
          console.log('✅ User is admin, allowing access');
        } else {
          console.log('❌ Failed to fetch role, redirecting to dashboard');
          next('/dashboard');
          return;
        }
      } catch (roleError) {
        console.error('💥 Error fetching role:', roleError);
        next('/dashboard');
        return;
      }
    }

    console.log('🎯 Allowing navigation to:', to.path);
    next();
  } catch (error) {
    console.error('💥 Auth check failed:', error);
    next('/sign-in');
  }
});

const app = createApp(App);
app.use(router);
app.use(vuetify);

// Initialize Google Analytics
analytics.init().then(() => {
  console.log('📊 Analytics service ready');
}).catch(error => {
  console.warn('📊 Analytics initialization failed:', error);
});

// Track page views automatically
router.afterEach(async (to) => {
  try {
    await analytics.trackPageView(to.path, to.name as string);
  } catch (error) {
    console.warn('📊 Failed to track page view:', error);
  }
});

// Fix for Vue 3 hydration mismatch in production
if (process.env.NODE_ENV === 'production') {
  // Set production performance mode
  app.config.performance = false;
  
  // Handle hydration mismatch gracefully
  app.config.errorHandler = (err: unknown, instance, info) => {
    if (err instanceof Error && err.message && err.message.includes('__VUE_PROD_HYDRATION_MISMATCH_DETAILS__')) {
      console.warn('Vue hydration mismatch detected, attempting recovery...');
      // Force re-render by unmounting and remounting
      setTimeout(() => {
        const appElement = document.getElementById('app');
        if (appElement) {
          appElement.innerHTML = '';
          app.mount('#app');
        }
      }, 100);
      return;
    }
    console.error('Vue error:', err, info);
  };
}

app.mount("#app");
